import './departmentSelectDialog.scss';
import { lang, commonLang } from '@umu/ukit-lang';
import { $window } from '@umu/umu-dom';
import React, { useReducer, useMemo, useRef } from 'react';
import { Button, ButtonType } from '@umu/umu-design';
import DListView, { IDListRef } from './components/@list/list';
import {
    DEPARTMENT_ACCESS,
    EnterpriseRoleType,
    EPlatformTravelType as TravelType,
    EPlatformSectionType as SectionType,
} from '@umu/umu-types';
import { DialogReducer } from './dialogReducer';
import { DialogContext, initState } from './dialogContext';

/**
 * DepartmentSelectDialog 部门选择弹窗
 *
 * Filter 模式（传入 onFilter）：
 *   - 右侧面板显示「已选部门 + 操作」表头，每个部门有「筛选 + 移除」按钮
 *   - 点击「筛选」打开 FilterDialog（复用 departmentSelectWithExcludeUser 的筛选弹窗）
 *   - 通过 defaultSelectedDepartmentList 传入已选部门及其 exclude_umu_ids 和缓存 oriData
 *   - 确认时按 selectedOrder 顺序返回数据
 *
 * 普通模式（不传 onFilter）：
 *   - 右侧面板显示原有的删除按钮，行为不变
 */
interface IProps {
    title: string;
    btnConfirmType?: ButtonType;
    departmentId: string;
    travelType: TravelType;
    isRadio?: boolean;
    expendParams?: { [propName: string]: string };
    excludeDepartmentIds?: string;
    defaultSelectedList?: string;
    source?: 'department_board';
    isAllowSelect?: boolean;
    checkableRoot?: boolean;
    allSelectText?: string;
    isShowMember?: boolean;
    hasAllPermissionRoles?: EnterpriseRoleType[];
    limit?: number;
    access?: DEPARTMENT_ACCESS;
    onFilter?: (item: any) => void;
    defaultSelectedDepartmentList?: { id: string; exclude_umu_ids: string[]; oriData?: any }[];
    cbClose?: () => void;
    cbDo?: (data: any[]) => void;
}

export default function DepartmentSelectDialog(props: IProps) {
    const hasAllPermissionRoles = useMemo(
        () => props.hasAllPermissionRoles || [EnterpriseRoleType.MANAGE, EnterpriseRoleType.ADMIN],
        [props.hasAllPermissionRoles],
    );
    const [state, dispatch] = useReducer(DialogReducer, { ...initState, hasAllPermissionRoles });
    const refList = useRef<IDListRef>(null);

    const onConfirm = () => {
        if (!props.cbDo) {
            return;
        }
        const selectList: any[] = [];
        if (props.onFilter) {
            state.selectedOrder.forEach((id) => {
                const value = state.selectedMap[id];
                if (value?.oriData) {
                    selectList.push(value.oriData);
                }
            });
        } else {
            Object.values(state.selectedMap).forEach((value) => {
                if (value.oriData) {
                    selectList.push(value.oriData);
                }
            });
        }
        const funCb = props.cbDo;
        $window.setTimeout(() => {
            funCb(selectList);
        }, 100);
    };

    const onClose = () => {
        if (refList?.current) {
            refList.current.cleanStateByParent();
        }
        if (props.cbClose) {
            const funCb = props.cbClose;
            $window.setTimeout(() => {
                funCb();
            }, 100);
        }
    };

    const title = props.title;
    const btnConfirmType = props.btnConfirmType || 'primary';
    const subTitleLeft = lang('待选');
    const subTitleRight = lang('已选');

    return (
        <DialogContext.Provider value={{ state, dispatch }}>
            <div className="department-select-dialog" title="">
                <div className="content-head">
                    <p>{title}</p>
                </div>
                <div className="content-body">
                    <div className="left">
                        <span className="subtitle">{subTitleLeft}</span>
                        <div className="subcontent">
                            <DListView
                                access={props.access}
                                type={props.travelType}
                                section={SectionType.left}
                                departmentId={props.departmentId}
                                isRadio={props.isRadio}
                                expendParams={props.expendParams}
                                ref={refList}
                                defaultSelectedList={props.defaultSelectedList}
                                defaultSelectedDepartmentList={props.defaultSelectedDepartmentList}
                                excludeDepartmentIds={props.excludeDepartmentIds}
                                isAllowSelect={props.isAllowSelect}
                                isShowMember={props.isShowMember}
                                allSelectText={props.allSelectText}
                                checkableRoot={props.checkableRoot}
                                limit={props.limit}
                                clsName="left"
                                hasAllPermissionRoles={hasAllPermissionRoles}
                            />
                        </div>
                    </div>
                    <div className="right">
                        <span className="subtitle">{subTitleRight}</span>
                        <div className={`subcontent${props.onFilter ? ' subcontent-filter' : ''}`}>
                            <DListView
                                access={props.access}
                                type={props.travelType}
                                section={SectionType.right}
                                departmentId={props.departmentId}
                                isRadio={props.isRadio}
                                checkableRoot={props.checkableRoot}
                                clsName="right"
                                hasAllPermissionRoles={hasAllPermissionRoles}
                                defaultSelectedList={props.defaultSelectedList}
                                onFilter={props.onFilter}
                                defaultSelectedDepartmentList={props.defaultSelectedDepartmentList}
                            />
                        </div>
                    </div>
                </div>
                <div className="dialog-content-footer">
                    <Button className="dialog-footer-btn" type={btnConfirmType} onClick={onConfirm}>
                        {commonLang('确定')}
                    </Button>
                    <Button className="dialog-footer-btn" type="ghost-grey" onClick={onClose}>
                        {commonLang('取消')}
                    </Button>
                </div>
            </div>
        </DialogContext.Provider>
    );
}
